import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Button } from 'components/ui';
import {
  ORIENTATION_DATE,
  PRIORITY_DEADLINE,
  PROGRAM_DATES,
  REGULAR_DEADLINE,
} from 'constants/dates.constants';
import {
  sclaApplicationStatusLabel,
  sclaShowsTxterBadge,
  type SclaApplicationStatusLabel,
} from 'utils/sclaApplicationKind';
import { getSclaAppBackgroundPath, withSclaCohortYear } from 'utils/navigation';
import type { SclaApplicationYear } from '../../api/getSclaApplicationYears';
import type { SclaApplicationSummary } from '../../api/listSclaApplications';
import {
  sclaHomeChecklist,
} from '../../config/homeChecklist';
import SclaStepMark from '../steps/SclaStepMark';
import type { SclaApplication } from '../../types';
import { sclaStepState, type SclaStepId } from '../../utils/sectionStatus';
import SclaPastApplications from './SclaPastApplications';
import SclaProgramIntro from './SclaProgramIntro';
import shortSclaScheduleDate from './shortSclaScheduleDate';
import styles from './SclaHome.module.scss';

const STATUS_TONE = {
  'not started': 'neutral',
  'in progress': 'warning',
  submitted: 'brand',
} as const;

const STATUS_TEXT: Record<SclaApplicationStatusLabel, string> = {
  'not started': 'Not started',
  'in progress': 'In progress',
  submitted: 'Submitted',
};

interface ISclaStartedHomeProps {
  year: SclaApplicationYear;
  application: SclaApplication;
  userType: string;
  pastApplications: SclaApplicationSummary[];
}

interface ISclaDateValueProps {
  value: string;
}

function SclaDateValue({ value }: ISclaDateValueProps) {
  const [start, end] = value.split(' - ');
  if (!end) {
    return <span className={styles.datePart}>{value}</span>;
  }
  return (
    <span className={styles.dateRange}>
      <span className={styles.datePart}>{`${start} -`}</span>
      <span className={styles.datePart}>{end}</span>
    </span>
  );
}

function actionFor(
  status: SclaApplicationStatusLabel,
  yearOpen: boolean,
): 'Start' | 'Continue' | 'View' {
  if (!yearOpen || status === 'submitted') return 'View';
  if (status === 'not started') return 'Start';
  return 'Continue';
}

export default function SclaStartedHome({
  year,
  application,
  userType,
  pastApplications,
}: ISclaStartedHomeProps) {
  const navigate = useNavigate();
  const status = sclaApplicationStatusLabel(application);
  const checklist = sclaHomeChecklist();
  const nextSection = checklist.find(section => {
    const state = sclaStepState(application, section.id);
    return state !== 'complete' && state !== 'optional';
  }) || checklist.find(section => section.id === 'signature') || checklist[0];
  const yearOpen = year.status === 'open';
  const action = actionFor(status, yearOpen);
  const continuePath = yearOpen
    ? nextSection.to()
    : withSclaCohortYear(getSclaAppBackgroundPath(), year.cohortYear);

  return (
    <>
      <div className={styles.titleRow}>
        <h1 className={`${styles.pageTitle} txt-title`}>SCLA Application</h1>
        {sclaShowsTxterBadge(application, userType) ? <Badge tone="brand">TXTer</Badge> : null}
      </div>

      <SclaProgramIntro />

      <section className={`${styles.card} ${styles.current}`}>
        <div className={styles.currentMain}>
          {yearOpen ? (
            <Badge tone="success">
              {year.cohortYear} · Accepting applications
            </Badge>
          ) : (
            <Badge tone="neutral">Applications closed</Badge>
          )}
          <h2 className={`${styles.appTitle} txt-heading`}>
            Your {year.cohortYear} application
            <Badge tone={STATUS_TONE[status]}>{STATUS_TEXT[status]}</Badge>
          </h2>
          <p>
            <Button onClick={() => navigate(continuePath)}>{action}</Button>
          </p>
          <dl className={`${styles.dates} txt-body`}>
            <dt>Priority deadline</dt>
            <dd>
              <SclaDateValue value={shortSclaScheduleDate(PRIORITY_DEADLINE)} />
            </dd>
            <dt>Final deadline</dt>
            <dd>
              <SclaDateValue value={shortSclaScheduleDate(REGULAR_DEADLINE)} />
            </dd>
            <dt>Orientation</dt>
            <dd>
              <SclaDateValue value={shortSclaScheduleDate(ORIENTATION_DATE)} />
            </dd>
            <dt>Program dates</dt>
            <dd>
              <SclaDateValue value={shortSclaScheduleDate(PROGRAM_DATES)} />
            </dd>
          </dl>
        </div>
        <div className={styles.checklist}>
          <h2 className={`${styles.sectionTitle} txt-heading`}>Checklist</h2>
          <ol className={`${styles.steps} txt-body`}>
            {checklist.map((section, index) => {
              const state = sclaStepState(application, section.id as SclaStepId);
              const href = yearOpen
                ? section.to()
                : withSclaCohortYear(section.to(), year.cohortYear);
              return (
                <li className={styles.step} key={section.id}>
                  <SclaStepMark state={state} index={index} />
                  <Link className={styles.stepLink} to={href}>
                    {section.label}
                  </Link>
                </li>
              );
            })}
          </ol>
        </div>
      </section>

      <SclaPastApplications applications={pastApplications} />
    </>
  );
}
